import React from 'react';
import {
  Box, Button, Flex, Spinner, Text,
} from '@chakra-ui/react';
import { LuMonitor } from 'react-icons/lu';
import { formatAmount } from '../../utils/goals';
import usePhoneHomeCurrency from '../../hooks/usePhoneHomeCurrency';
import DesktopOnlyNote from './DesktopOnlyNote';
import PhoneMovementRow from './PhoneMovementRow';

// Kept here too for the pages that already import it from this module.
export { usePhoneHomeCurrency };

/**
 * Pieces shared by the read-only phone pages (Categories, Tags, Goals) and
 * their detail sheets, so the three read alike.
 */

/** "COP 498,700": the code, then the whole amount in the US layout. */
export const money = (currency, amount) => (
  currency ? `${currency} ${formatAmount(amount)}` : formatAmount(amount)
);

// A card's ground: the surface on the page; inside a sheet, which already is
// the surface, the fill PhoneSheet names in --phone-card-bg.
const CARD_BG = 'var(--phone-card-bg, var(--chakra-colors-redesign-bg-surface))';

/** Rows on a card, as iOS lists draw them, with hairlines between them. */
export function ListCard({ children, mt = '12px', dividerInset = '68px' }) {
  return (
    <Box
      mx="16px"
      mt={mt}
      borderRadius="14px"
      bg={CARD_BG}
      border="1px solid"
      borderColor="redesign.border"
      overflow="hidden"
      sx={{
        '& > * + *': { position: 'relative' },
        '& > * + *::before': {
          content: '""', position: 'absolute', top: 0, left: dividerInset, right: 0,
          height: '1px', bg: 'redesign.border',
        },
      }}
    >
      {children}
    </Box>
  );
}

/** "SEPTEMBER · 9 MOVEMENTS": the small uppercase line over a list. */
export function SectionCaption({ children, pt = '24px' }) {
  return (
    <Text textStyle="phoneCaption" color="phone.muted" px="16px" pt={pt} pb="8px">
      {children}
    </Text>
  );
}

/** A 40px (or larger) disc holding an emoji, tinted with the item's colour. */
export function Disc({ children, tint, size = 40, fontSize = '19px' }) {
  return (
    <Flex
      w={`${size}px`}
      h={`${size}px`}
      borderRadius="full"
      align="center"
      justify="center"
      flexShrink={0}
      bg={tint || 'redesign.bg.hover'}
      fontSize={fontSize}
      lineHeight="1"
    >
      {children}
    </Flex>
  );
}

/** The one line saying where editing lives, on a card with a screen icon. */
export function DesktopNoteCard({ mt = '24px', message }) {
  return (
    <Flex
      mx="16px"
      mt={mt}
      align="center"
      pl="14px"
      borderRadius="12px"
      bg={CARD_BG}
      border="1px solid"
      borderColor="redesign.border"
      color="phone.muted"
    >
      <Box as={LuMonitor} boxSize="18px" flexShrink={0} />
      <Box flex="1" minW={0} ml="-6px"><DesktopOnlyNote message={message} /></Box>
    </Flex>
  );
}

/**
 * This month against the 6-month average: a 4px track whose fill is the
 * month and whose tick is the average. The scale leaves the average at 5/6
 * of the width, so both over and under read at a glance.
 */
export function AverageBar({ value, average, fill, h = '4px' }) {
  const scale = Math.max(average * 1.2, value, 1);
  const width = Math.min(100, (value / scale) * 100);
  const tick = (average / scale) * 100;
  return (
    <Box position="relative" h={h} borderRadius="full" bg="redesign.border" w="100%">
      <Box position="absolute" left={0} top={0} bottom={0} w={`${width}%`} borderRadius="full" bg={fill} />
      {average > 0 && (
        <Box
          position="absolute"
          left={`${tick}%`}
          top="-4px"
          w="2px"
          h={`calc(${h} + 8px)`}
          borderRadius="1px"
          bg="redesign.text"
        />
      )}
    </Box>
  );
}

/** Centred content for loading, error and empty states. */
export function PhoneState({ loading, children }) {
  return (
    <Flex direction="column" align="center" justify="center" py="48px" px="24px" textAlign="center" gap="12px">
      {loading && <Spinner size="md" color="phone.muted" />}
      {children && <Text textStyle="phoneBody" color="phone.muted">{children}</Text>}
    </Flex>
  );
}

/** A 44px row that shows or hides the rest of a list. */
export function ToggleRow({ children, onClick }) {
  return (
    <Button
      variant="ghost"
      w="calc(100% - 32px)"
      mx="16px"
      mt="8px"
      h="44px"
      fontSize="15px"
      fontWeight="500"
      color="phone.link"
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

/**
 * A detail sheet's movements, as the phone rows everywhere else: one tap
 * opens the movement sheet over the detail. `onMore` adds a 48px "Show more".
 */
export function MovementRows({
  movements, loading, emptyLabel, onOpen, onMore, moreLabel, loadingMore, homeCurrency,
}) {
  const resolvedHome = usePhoneHomeCurrency();
  const home = homeCurrency || resolvedHome;
  if (loading && movements.length === 0) return <PhoneState loading />;
  if (movements.length === 0) {
    return <Text textStyle="phoneMeta" color="phone.muted" px="16px" py="12px">{emptyLabel}</Text>;
  }
  return (
    <Box>
      {movements.map((m) => (
        <PhoneMovementRow key={m.id} movement={m} homeCurrency={home} showDate onClick={() => onOpen(m.id)} />
      ))}
      {onMore && (
        <Button
          variant="ghost"
          w="100%"
          h="48px"
          fontSize="15px"
          fontWeight="500"
          color="phone.link"
          isLoading={loadingMore}
          onClick={onMore}
        >
          {moreLabel}
        </Button>
      )}
    </Box>
  );
}

/** Two or three choices on one 44px track, as iOS segmented controls. */
export function Segments({ options, value, onChange, label }) {
  return (
    <Flex
      role="radiogroup"
      aria-label={label}
      bg="redesign.bg.sunken"
      border="1px solid"
      borderColor="redesign.border"
      borderRadius="12px"
      p="2px"
      gap="2px"
      w="100%"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <Flex
            key={opt.value}
            as="button"
            type="button"
            role="radio"
            aria-checked={active}
            flex="1"
            minW={0}
            h="44px"
            align="center"
            justify="center"
            gap="6px"
            borderRadius="9px"
            fontSize="15px"
            fontWeight={active ? '600' : '500'}
            bg={active ? 'redesign.bg.raised' : 'transparent'}
            color={active ? 'redesign.heading' : 'phone.muted'}
            boxShadow={active ? 'sm' : 'none'}
            onClick={() => onChange(opt.value)}
          >
            {opt.icon && <Box as={opt.icon} boxSize="16px" flexShrink={0} />}
            <Box as="span" noOfLines={1}>{opt.label}</Box>
          </Flex>
        );
      })}
    </Flex>
  );
}
